<template>
  <section id="works" class="common-padding mb-20">
    <div class="flex flex-col">
      <h3
        id="selectedWorks"
        v-html="selectedWorks"
        class="heading-1 text-start leading-none font-bold uppercase"
      ></h3>
      <p
        class="heading-1 text-flax-smoke-400 text-opacity-50 hidden w-4/5 text-end font-extrabold sm:block"
      >
        ( {{ selectedWorksProps.length }} )
      </p>

      <div
        id="selected-works-text"
        class="md:column-gap text-flax-smoke-300 mt-[5%] grid grid-cols-12 justify-end opacity-0 lg:grid"
      >
        <p
          class="heading-6 text-flax-smoke-300/85 col-span-4 text-center text-nowrap lg:col-start-2"
        >
          (
          <span class="inline sm:hidden">{{ selectedWorksProps.length }} </span>
          PROJECTS )
        </p>
        <p
          class="heading-4 font-fancy col-span-8 w-full text-balance sm:font-semibold lg:col-span-7"
        >
          Personal, independent projects I've designed and built on my own
          time — from web platforms to apps published on the App Store and
          Play Store.
        </p>
      </div>
    </div>

    <div
      class="sm:column-gap relative mt-12 grid size-full grid-cols-12 lg:mt-[10%]"
    >
      <div
        class="text-flax-smoke-100 sticky top-12 col-span-5 hidden h-fit w-full overflow-hidden text-[22vw] leading-[0.8] font-semibold md:flex"
      >
        <span class="font-title! relative -tracking-wider">0</span>
        <span
          id="index"
          class="font-title! relative -tracking-wider will-change-transform"
          >{{ index + 1 }}.</span
        >
      </div>
      <aside
        @mouseenter="showCursor"
        @mouseleave="hideCursor"
        class="relative col-span-full flex flex-col space-y-10 md:col-span-7"
      >
        <div
          v-for="(work, i) in selectedWorksProps"
          :key="i"
          class="work-card @container"
        >
          <a class="group" target="_blank" :href="work.url">
            <div
              class="flex-center relative aspect-square overflow-clip rounded-lg"
            >
              <img
                alt=""
                aria-hidden="true"
                loading="lazy"
                class="absolute size-full scale-110 object-cover object-center blur-2xl select-none"
                :src="work.image"
              />
              <img
                :alt="work.name + ' preview'"
                loading="lazy"
                class="relative z-10 max-h-[70%] max-w-[70%] rounded-md object-contain shadow-2xl select-none"
                :src="work.image"
              />
            </div>
            <div>
              <p class="heading-6 font-title! mt-[2%] mb-[1%] leading-none">
                {{ work.category }}
              </p>
              <div class="items-center justify-between sm:flex">
                <h3 class="heading-3 font-title! font-bold uppercase">
                  {{ work.name }}
                </h3>
                <div class="flex gap-1.5 select-none">
                  <p
                    class="border-flax-smoke-300 hover:bg-flax-smoke-300 hover:text-flax-smoke-900 rounded-full border px-4 py-2 transition-[background-color,color] duration-500 ease-in-out"
                    v-for="tag in work.tags"
                    :key="tag"
                  >
                    <span>{{ tag }}</span>
                  </p>
                </div>
              </div>
            </div>
          </a>
        </div>
      </aside>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { animateSplitText } from '@/animations';
  import { textSplitterIntoChar } from '@/functions';
  import { computed, onBeforeMount, onMounted, ref } from 'vue';
  import gsap from 'gsap';
  import { useWindowSize } from '@vueuse/core';

  const isSmallScreen = computed(() => {
    return useWindowSize().width.value < 768;
  });
  const index = ref(0);
  const selectedWorks = ref('Selected Projects /');

  const tl = gsap
    .timeline({ defaults: { duration: 0.25 } })
    .to(['#cursor', '#inner'], {
      scale: 1,
      opacity: 1,
    })
    .paused(true);

  const showCursor = () => {
    tl.play();
  };
  const hideCursor = () => {
    tl.reverse();
  };

  const selectedWorksProps = [
    {
      name: 'Yesis Global',
      category: 'Web App',
      tags: ['Next.js', 'GSAP', 'Framer Motion'],
      image: '/assets/projects/yesisglobal.png',
      url: 'https://yesis-global-sandy.vercel.app',
    },
    {
      name: 'You or Them',
      category: 'Mobile App',
      tags: ['Flutter', 'Flame Game Engine'],
      image:
        'https://is1-ssl.mzstatic.com/image/thumb/PurpleSource211/v4/5f/3f/66/5f3f6695-a6bb-8528-3555-ed5dc1856ef8/Placeholder.mill/400x400bb-75.webp',
      url: 'https://apps.apple.com/us/app/you-or-them/id6781589956',
    },
    {
      name: 'JarVest',
      category: 'Mobile App',
      tags: ['Flutter', 'RevenueCat'],
      image:
        'https://play-lh.googleusercontent.com/bTcdEzdlqw6COV2UlfFho3YTkWPZx_kr02nmFzV-aXcwrsqdQpJFs4HcyxSUHFF_DgI8Bv7M_8i6uY-q2kAKFHk=w480-h960',
      url: 'https://apps.apple.com/us/app/jarvest/id6775189188',
    },
    {
      name: 'Reni',
      category: 'Mobile App',
      tags: ['React Native', 'Supabase', 'Gemini'],
      image:
        'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/30/ec/71/30ec715d-6bce-0146-ab09-95ee478c5cf8/AppIcon-0-0-1x_U007epad-0-1-85-220.png/200x200ia-75.webp',
      url: 'https://apps.apple.com/us/app/reni/id6752829181',
    },
    {
      name: 'Forward to the Past',
      category: 'Mobile App',
      tags: ['React Native', 'Gemini'],
      image:
        'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/09/f1/75/09f175ac-30d2-51aa-9725-0ae7308a6677/AppIcon-0-0-1x_U007epad-0-1-85-220.png/200x200ia-75.webp',
      url: 'https://apps.apple.com/us/app/forward-to-the-past/id6752709892',
    },
    {
      name: 'Acar Architecture',
      category: 'Web App',
      tags: ['React', 'Tailwind CSS', 'Supabase'],
      image: '/assets/projects/acar.png',
      url: 'https://mimarlikacar.com',
    },
  ];

  // Reusable function to handle forward scroll animation
  const createForwardTimeline = (
    index: any,
    i: any,
    selectedWorksProps: any[],
  ) => {
    const tl = gsap.timeline({
      defaults: { duration: 0.3 },
    });

    // Set and move the #index element
    tl.set('#index', {
      yPercent: 100,
      onComplete: () => {
        index.value = Math.min(i, selectedWorksProps.length - 1);
      },
    }).to('#index', {
      yPercent: 0,
      ease: 'power1.inOut',
    });

    return tl;
  };

  // Reusable function to handle backward scroll animation
  const createBackwardTimeline = (index: any, i: any) => {
    const tl = gsap.timeline({ defaults: { duration: 0.3 } });

    // Set and move the #index element
    tl.set('#index', {
      yPercent: -100,
      onComplete: () => {
        index.value = Math.max(i, 0);
      },
    }).to('#index', {
      yPercent: 0,
      duration: 0.3,
      ease: 'power1.inOut',
    });

    return tl;
  };

  onBeforeMount(() => {
    selectedWorks.value = textSplitterIntoChar('Selected Works / ', true);
  });

  onMounted(() => {
    animateSplitText(
      '#selectedWorks .letters',
      '#selected-works-text',
      0.7,
      0.01,
      0,
    );

    // Apply GSAP animations to each div
    if (!isSmallScreen.value)
      gsap.utils.toArray('.work-card').forEach((div: any, i: any) => {
        gsap.timeline({ defaults: { duration: 0.7 } }).to(div, {
          scrollTrigger: {
            trigger: div,
            start: 'top 25%',
            end: 'bottom 25%',
            scrub: 0.01,
            onLeaveBack: () => {
              // Backward scroll animation
              if (index.value !== 0) {
                gsap.to('#index', {
                  yPercent: 100,
                  duration: 0.3,
                  ease: 'power4.inOut',
                  onComplete: () => {
                    createBackwardTimeline(index, i - 1);
                  },
                });
              }
            },
          },
          ease: 'power1.inOut',
          onComplete: () => {
            // Forward scroll animation
            if (index.value !== selectedWorksProps.length - 1) {
              gsap.to('#index', {
                yPercent: -100,
                duration: 0.3,
                ease: 'power4.inOut',
                onComplete: () => {
                  createForwardTimeline(index, i + 1, selectedWorksProps);
                },
              });
            }
          },
        });
      });
  });
</script>
